import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, LockKey, User, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError, saveToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import PageShell from "@/components/PageShell";

// Anonymous team-member join flow:
// - No signup required
// - The backend creates a lightweight guest user + issues a JWT
// - We save the token via loginWithToken so subsequent team calls work
export default function JoinTeam() {
  const { user, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [teamId, setTeamId] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { team_id: teamId.toUpperCase(), password };
      // Authenticated users skip display_name (server uses their profile name)
      if (!user) payload.display_name = displayName.trim();

      const { data } = await api.post("/teams/join", payload);

      // Guest join returns { team, token, user }; authed join returns team directly.
      const team = data.team || data;
      if (data.token) {
        await loginWithToken(data.token, data.user);
        saveToken(data.token);
      }
      toast.success(`Joined "${team.name}"`);
      trackEvent("team_joined", { team_id: team.team_id, guest: !user });
      navigate(`/teams/${team.team_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell
      eyebrow="Join team"
      title="Got an invite?"
      subtitle={
        user
          ? "Enter the Team ID and password your team leader shared with you."
          : "No signup needed — just enter the Team ID, password, and a display name so your team knows who you are."
      }
      narrow
    >
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-start">
        <form onSubmit={submit} className="brut-card p-6 space-y-5" data-testid="join-team-form">
          <div>
            <div className="small-caps mb-2 flex items-center gap-1"><Users size={12}/> Team ID</div>
            <input
              data-testid="join-team-id"
              className="input-brut w-full font-mono uppercase tracking-[0.3em] text-lg"
              required minLength={4} maxLength={12}
              value={teamId}
              onChange={(e) => setTeamId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="6KX4MFRC"
            />
          </div>

          <div>
            <div className="small-caps mb-2 flex items-center gap-1"><LockKey size={12}/> Team password</div>
            <input
              data-testid="join-team-password"
              className="input-brut w-full" type="password" required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {!user && (
            <div data-testid="join-display-name-wrap">
              <div className="small-caps mb-2 flex items-center gap-1"><User size={12}/> Your name</div>
              <input
                data-testid="join-display-name"
                className="input-brut w-full"
                required minLength={2} maxLength={40}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Priya · Alex · etc."
              />
              <div className="text-[11px] text-black/55 mt-1 font-mono">
                Shown next to files you upload. No account created.
              </div>
            </div>
          )}

          <button
            data-testid="join-team-submit" type="submit" disabled={busy}
            className="btn-brut-primary w-full disabled:opacity-50"
          >
            {busy ? "Joining…" : "Join team"} <ArrowRight size={16} weight="bold"/>
          </button>
        </form>

        <aside className="brut-card p-6 space-y-3">
          <div className="small-caps">Don't have a Team ID?</div>
          <p className="text-sm text-black/75">
            Ask the team leader to share the 8-character Team ID and the team password. Only the
            leader has to sign in — members join anonymously.
          </p>
          <div className="mt-5 small-caps">Want to start your own?</div>
          <Link to="/teams/create" className="btn-brut-accent w-full">
            Create a team <ArrowRight size={16}/>
          </Link>
        </aside>
      </div>
    </PageShell>
  );
}
